import React, { useState, useEffect } from 'react';
import { FiArrowRight, FiArrowLeft, FiCheck, FiDollarSign, FiCalendar, FiLock, FiRefreshCw, FiTrendingUp } from 'react-icons/fi';
import WrapUpP2p from './layouts/WrapUpP2p';
import { useDispatch, useSelector } from 'react-redux';
import { allRemoteSettingsSelect } from '../../../redux/selectors';
import { money, notify } from '../../../utility/Helper1';
import { exchangeActions } from '../../../redux/actions';
import { useNavigate } from 'react-router-dom';


const ModernInvestmentFlow = () => {
  const site_settings = useSelector(allRemoteSettingsSelect);
  const currencies = site_settings?.plans;
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState(1000);
  const [selectedCurrency, setSelectedCurrency] = useState(currencies?.[0]?.mother?.type);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [submittingInvestment, setSubmittingInvestment] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  
  const allPlans = site_settings?.plans?.filter(el => el.mother?.type === selectedCurrency);
  const selectedCurrencyObj = allPlans?.[0]?.mother;

  // Calculate projected returns based on p2p_roi
  const calculateReturns = () => {
    if (!selectedPlan) return { returns: 0, total: 0 };
    
    // Calculate returns for the investment period
    const investmentDays = parseInt(selectedPlan?.p2p_ghdate) || 30; // Default to 30 if not specified
    const periodRoi =  parseFloat(selectedPlan?.p2p_roi ?? 0)
    

    const investmentAmount = parseFloat(amount);
    const returns = (investmentAmount * periodRoi / 100);
    const total = returns;
    
    return {
      returns,
      total,
      periodRoi: periodRoi?.toFixed(2),
      daysText: `${investmentDays} days (${(periodRoi)}% return)`
    };
  };

  const projectedReturns = calculateReturns();

  useEffect(() => {
    // Set default plan when currency changes
    if (allPlans?.[0]?.childs?.length > 0 && !selectedPlan) {
      // setSelectedPlan(allPlans[0].childs[0]);
    }
  }, [selectedCurrency, allPlans]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setAmount(plan.p2p_minimumdonation || 1000);
    setStep(2);
  };

  const isAmountValid = selectedPlan && parseFloat(amount) >= parseFloat(selectedPlan.p2p_minimumdonation) && parseFloat(amount) <= parseFloat(selectedPlan.p2p_maximumdonation);

  const handleStepChange = (newStep) => {
    if (newStep < step) {
      setStep(newStep);
    } else if (
      (newStep === 2 && selectedPlan) ||
      (newStep === 3 && isAmountValid)
    ) {
      setStep(newStep);
    }
  };


  const submitInvestment = () => {

    setSubmittingInvestment(true)
    const data = {
      amount,
      plan: selectedPlan?.type
    }

    dispatch(exchangeActions.onPostExchange(data, "p2p/exchange/new", response => {

      setSubmittingInvestment(false)
      notify(response.msg, response.code)

      if(response?.code == 1){
        navigate('/portfolio')
      }

    }))

  }


  return (
    <WrapUpP2p>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8 flex flex-col items-center">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-md p-6 md:p-8">
          {/* Progress Steps */}
          <div className="flex justify-between items-center mb-10 relative">
            <div className="absolute top-1/2 h-1 bg-gray-200 w-full -z-10"></div>
            {[1, 2, 3].map((i) => (
              <button 
                key={i} 
                className="flex flex-col items-center focus:outline-none relative"
                onClick={() => handleStepChange(i)}
                disabled={i > step}
              >
                <div 
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                    i <= step ? 
                      i < step ? 'border-emerald-500 bg-emerald-500 text-white' : 
                      'border-emerald-500 bg-white text-emerald-500' : 
                    'border-gray-300 bg-white text-gray-400'} transition-colors duration-300 shadow-sm`}
                >
                  {i < step ? <FiCheck size={20} /> : i}
                </div>
                <span className={`text-xs font-medium mt-2 ${i <= step ? 'text-gray-800' : 'text-gray-400'}`}>
                  {i === 1 ? 'Select Plan' : i === 2 ? 'Set Amount' : 'Confirm'}
                </span>
              </button>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <div className="flex justify-between items-end mb-2">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Choose Your Plan</h2>
                  <p className="text-gray-600 text-sm mt-1">Select an investment strategy that works for you</p>
                </div>
                {currencies?.length > 1 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Currency:</span>
                    <select
                      value={selectedCurrency}
                      onChange={(e) => {
                        setSelectedCurrency(e.target.value);
                        setSelectedPlan(null);
                      }}
                      className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    >
                      {currencies?.map(currency => (
                        <option key={currency?.mother?.abbrRo} value={currency?.mother?.type}>
                          {currency?.mother?.abbrRo}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                {allPlans?.[0]?.childs?.map((plan) => (
                  <div
                    key={plan.type}
                    onClick={() => handlePlanSelect(plan)}
                    className={`p-5 rounded-xl border-2 ${
                      selectedPlan && ((selectedPlan?.type ?? 0) == plan.type) 
                        ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100' 
                        : 'border-gray-200 hover:border-emerald-200 hover:bg-gray-50'
                    } cursor-pointer transition-all duration-200 transform hover:-translate-y-1`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <h3 className="font-semibold text-lg text-gray-900">{plan.name}</h3>
                          <span className={`ml-2 px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800`}>
                            {plan.p2p_roi}% ROI
                          </span>
                        </div>
                        
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <FiDollarSign className="mr-1.5 text-emerald-500" size={14} />
                            <span>Min: {money(plan?.p2p_minimumdonation, selectedCurrencyObj?.type)}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <FiDollarSign className="mr-1.5 text-emerald-500" size={14} />
                            <span>Max: {money(plan?.p2p_maximumdonation, selectedCurrencyObj?.type)}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <FiCalendar className="mr-1.5 text-emerald-500" size={14} />
                            <span>{plan.p2p_ghdate} days</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <FiTrendingUp className="mr-1.5 text-emerald-500" size={14} />
                            <span>{plan.p2p_roi}% Return</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 ${
                        selectedPlan?.type === plan.type 
                          ? 'border-emerald-500 bg-emerald-500' 
                          : 'border-gray-300'
                      }`}>
                        {selectedPlan?.type === plan.type && (
                          <FiCheck className="text-white" size={20} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && selectedPlan && (
            <div className="space-y-6">
              <div>
                <button 
                  onClick={() => handleStepChange(1)}
                  className="flex items-center text-sm text-emerald-600 mb-4 hover:text-emerald-700 font-medium"
                >
                  <FiArrowLeft className="mr-1.5" /> Back to Plans
                </button>
                <h2 className="text-2xl font-bold text-gray-900">Investment Amount</h2>
                <div className="flex items-center mt-1">
                  <span className="text-emerald-600 font-medium mr-2">{selectedPlan.name} Plan</span>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                    {selectedPlan.p2p_roi}% ROI
                  </span>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">How much would you like to invest?</label>
                  <div className="relative">
                    {selectedCurrencyObj && (
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <span className="text-gray-500 font-medium">{selectedCurrencyObj?.abbrRo}</span>
                      </div>
                    )}
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      min={selectedPlan.p2p_minimumdonation}
                      max={selectedPlan.p2p_maximumdonation}
                      className={`w-full ${selectedCurrencyObj ? 'pl-14' : 'pl-4'} pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm`}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className={`${amount < selectedPlan.p2p_minimumdonation ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                      Min: {money(selectedPlan.p2p_minimumdonation, selectedCurrencyObj?.type)}
                    </span>
                    <span className={`${amount > selectedPlan.p2p_maximumdonation ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                      Max: {money(selectedPlan.p2p_maximumdonation, selectedCurrencyObj?.type)}
                    </span>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Investment Period:</span>
                    <span className="font-medium text-gray-900">{selectedPlan.p2p_ghdate} days</span>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Projected Return:</span>
                      <span className="font-medium text-emerald-600">
                        {isAmountValid ? `${money(projectedReturns.returns, selectedCurrencyObj?.type)} (${projectedReturns?.periodRoi}%)` : '--'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="pt-2">
                  <button
                    onClick={() => handleStepChange(3)}
                    disabled={!isAmountValid}
                    className={`w-full py-4 ${
                      isAmountValid ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-emerald-300 cursor-not-allowed'
                    } text-white font-medium rounded-xl flex items-center justify-center space-x-2 transition-colors shadow-sm`}
                  >
                    <span>Continue to Review</span>
                    <FiArrowRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 3 && selectedPlan && (
            <div className="space-y-6">
              <div>
                <button 
                  onClick={() => handleStepChange(2)}
                  className="flex items-center text-sm text-emerald-600 mb-4 hover:text-emerald-700 font-medium"
                >
                  <FiArrowLeft className="mr-1.5" /> Back to Amount
                </button>
                <h2 className="text-2xl font-bold text-gray-900">Review & Confirm</h2>
                <p className="text-gray-600 text-sm mt-1">Final check before investing</p>
              </div>
              
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100 overflow-hidden shadow-sm">
                <div className="p-5 bg-emerald-600 text-white">
                  <h3 className="font-semibold text-lg">{selectedPlan.name} Investment</h3>
                  <p className="text-emerald-100 text-sm">{projectedReturns.daysText}</p>
                </div>
                
                <div className="p-5 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Principal Amount</span>
                    <span className="font-semibold text-gray-900">
                      {money(amount, selectedCurrencyObj?.type)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Investment Period</span>
                    <span className="font-medium text-gray-900">{selectedPlan.p2p_ghdate} days</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Projected Return</span>
                    <span className="font-medium text-emerald-600">
                      {money(projectedReturns.returns, selectedCurrencyObj?.type)}
                    </span>
                  </div>
                  
                  <div className="border-t border-emerald-100 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Total at Maturity</span>
                      <span className="font-semibold text-lg text-emerald-600">
                        {money(projectedReturns.total, selectedCurrencyObj?.type)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              
              <div className="flex space-x-4 pt-2">
                <button
                  onClick={() => handleStepChange(2)}
                  className="flex-1 py-4 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={submitInvestment}
                  disabled={submittingInvestment}
                  className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl transition-colors shadow-sm relative"
                >
                  {submittingInvestment ? (
                    <>
                      <span className="opacity-0">Confirm Bid</span>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      </div>
                    </>
                  ) : (
                    'Confirm Bid'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </WrapUpP2p>
  );
};

export default ModernInvestmentFlow;